import React, { FunctionComponent, useContext, useEffect, useMemo } from "react";
import { Theme, makeStyles, Grid, useTheme } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  FeedbackType,
  NutrientsResponse,
  useMicronutrientsLazyQuery,
} from "../../../api/smartmelts-api/graphql/generated";
import { AnswerContext } from "../answer/context/answer-context";
import { QuestionSectionLoadingIndicator } from "../question/question-section-loading-indicator";
import { NutritionContext } from "../context/nutrition-context";
import { NutrientIndicator } from "./nutrient-indicator/nutrient-indicator";
import { HeadingText } from "../../../components/text/heading-text";
import { ContentText } from "../../../components/text/content-text";
import _ from "lodash";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    width: "100%",
  },
  indicatorContainer: {
    width: "100%",
  },
  emptyContainer: {
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(6),
  },
  contentText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
}));

interface IMicronutrientsAnswerProps {}

export const MicronutrientsAnswer: FunctionComponent<IMicronutrientsAnswerProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const { activeAnswer } = useContext(AnswerContext);
  const { isDemo, scrollTop } = useContext(NutritionContext);

  const [micronutrientsQuery, { loading, data }] = useMicronutrientsLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      scrollTop();
    },
  });

  useEffect(() => {
    if (activeAnswer) {
      micronutrientsQuery({ variables: { demo: isDemo } });
    }
  }, [micronutrientsQuery, activeAnswer, isDemo]);

  const nutrients: NutrientsResponse[] = useMemo(() => {
    if (data) {
      return _.orderBy(data.micronutrients, ({ name }) => t(`nutrient.values.${name}.name`), "asc");
    }
    return [];
  }, [data, t]);

  return (
    <Grid container className={classes.container}>
      <HeadingText>{t(`twin_communication.answer.${FeedbackType.MICRONUTRIENTS}.heading`)}</HeadingText>
      <Grid item className={classes.contentText}>
        <ContentText light>{t(`twin_communication.answer.${FeedbackType.MICRONUTRIENTS}.description`)}</ContentText>
      </Grid>
      <Grid container direction="column" spacing={4}>
        {!loading && !!nutrients.length && (
          <Grid item container spacing={4}>
            {nutrients?.map((nutrient, idx) => (
              <Grid item key={nutrient.name} sm={6} md={12} lg={6} className={classes.indicatorContainer}>
                <NutrientIndicator
                  nutrient={nutrient}
                  key={idx}
                  alwaysShowRda
                  hideRecommendation
                  borderColor={theme.palette.primary.main}
                />
              </Grid>
            ))}
          </Grid>
        )}
        {!loading && !nutrients.length && (
          <Grid item container justify="center" className={classes.emptyContainer}>
            <ContentText align="center">
              {t(`twin_communication.answer.${FeedbackType.NUTRIENTS_TO_INCREASE}.empty`)}
            </ContentText>
          </Grid>
        )}
        {loading && <QuestionSectionLoadingIndicator />}
      </Grid>
    </Grid>
  );
};
